import { Menu } from 'lucide-react'
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import * as React from 'react'

import { useBranding } from '@/hooks/use-branding'

import GroupChannelSidebar from '@/components/forum/group-channel-sidebar'
import PostDetailModal from '@/components/meme/post-detail-modal'
import SEO from '@/components/seo/SEO'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import ListMemeContainer, { SharedPostHandler } from '@/containers/meme/list-meme-container'
import RightSidebarMeme from '@/containers/meme/right-sidebar-meme'
import RootContainer from '@/containers/root-container'

import { useForumUnread } from '@/contexts/forum-unread-context'
import { MemePostModalProvider } from '@/contexts/meme-post-modal-context'
import memeService from '@/services/meme.service'
import { createServerSideApiConfig } from '@/utils/api.utils'
import { generateMemeSEO } from '@/utils/seo-utils'

import { MemePost } from '@/types/meme.interface'

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const { query } = context
  const apiConfig = createServerSideApiConfig(context)

  const channelId = (query?.channelId as string) ?? null
  const groupId = (query?.groupId as string) ?? null
  const postId = (query?.postId as string) ?? null

  let memeForSeo: MemePost | null = null
  if (postId) {
    try {
      const meme = await memeService.getMemeDetail(postId, apiConfig)
      memeForSeo = JSON.parse(JSON.stringify(meme)) as MemePost
    } catch {
      memeForSeo = null
    }
  }

  return {
    props: {
      channelId,
      groupId,
      memeForSeo,
    },
  }
}

export default function ForumPage({
  channelId,
  groupId,
  memeForSeo,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { siteName } = useBranding()
  const memeSeo = memeForSeo ? generateMemeSEO(memeForSeo) : null
  const forumCanonical = memeForSeo != null ? `/forum?postId=${encodeURIComponent(memeForSeo.id)}` : undefined

  return (
    <RootContainer showFooter={false}>
      {memeSeo ? (
        <SEO
          title={memeSeo.title}
          description={memeSeo.description}
          keywords={memeSeo.keywords}
          ogImage={memeSeo.ogImage}
          ogType="article"
          author={memeSeo.author}
          publishedTime={memeSeo.publishedTime}
          modifiedTime={memeSeo.modifiedTime}
          section={memeSeo.section}
          tags={memeSeo.tags}
          canonicalUrl={forumCanonical}
        />
      ) : (
        <SEO title={`Forum | ${siteName}`} />
      )}
      <MemePostModalProvider>
        <SharedPostHandler />
        <PostDetailModal />
        <ForumContainer channelId={channelId} groupId={groupId} />
      </MemePostModalProvider>
    </RootContainer>
  )
}

function ForumContainer({
  channelId: initialChannelId,
  groupId: initialGroupId,
}: {
  channelId?: string | null
  groupId?: string | null
}) {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false)

  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { resetChannel } = useForumUnread()

  const channelId = initialChannelId || searchParams.get('channelId') || undefined
  const groupId = initialGroupId || searchParams.get('groupId') || undefined

  React.useEffect(() => {
    if (channelId) {
      resetChannel(channelId)
    }
  }, [channelId, resetChannel])

  const onChannelSelect = React.useCallback(
    (nextChannelId: string, groupId: string, channelName: string) => {
      const params = new URLSearchParams(searchParams.toString())
      params.set('name', channelName)
      params.set('groupId', groupId)
      params.set('channelId', nextChannelId)
      params.delete('tags')
      router.push(`${pathname}?${params.toString()}`)
      setIsMobileSidebarOpen(false)
    },
    [pathname, router, searchParams]
  )

  return (
    <div className="h-full flex flex-col lg:flex-row">
      <MobileHeader
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        channelId={channelId}
        groupId={groupId}
        onChannelSelect={onChannelSelect}
      />

      <div className="hidden lg:block sticky top-20 z-10 self-start h-screen max-h-[calc(100vh-80px)] overflow-y-auto transition-all w-80 xl:w-96 flex-shrink-0">
        <GroupChannelSidebar currentChannelId={channelId} groupId={groupId} onSelectChannel={onChannelSelect} />
      </div>

      <div className="flex-1 min-w-0 lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100vh-80px)]">
        <div
          className="h-full overflow-y-auto p-4 sm:p-6 lg:p-8 bg-gray-100 max-h-[calc(100dvh-4rem)] lg:max-h-none"
          data-meme-feed-scroll
        >
          <div className="max-w-3xl mx-auto space-y-4">
            {channelId ? (
              <>
                <div className="xl:hidden">
                  <RightSidebarMeme channelId={channelId} />
                </div>
                <ListMemeContainer channelId={channelId} />
              </>
            ) : (
              <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
                <h2 className="text-xl font-semibold text-card-foreground mb-2">Pick a channel</h2>
                <p className="text-muted-foreground text-sm sm:text-base">
                  <span className="lg:hidden">Tap the menu button to select a channel and view posts.</span>
                  <span className="hidden lg:inline">Select a channel from the sidebar to view posts.</span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {channelId && (
        <div className="hidden xl:block sticky top-20 z-10 self-start h-screen max-h-[calc(100vh-80px)] overflow-y-auto transition-all w-96 flex-shrink-0 p-4">
          <RightSidebarMeme channelId={channelId} />
        </div>
      )}
    </div>
  )
}

function MobileHeader({
  isMobileSidebarOpen,
  setIsMobileSidebarOpen,
  channelId,
  groupId,
  onChannelSelect,
}: {
  isMobileSidebarOpen: boolean
  setIsMobileSidebarOpen: (open: boolean) => void
  channelId: string | undefined
  groupId: string | undefined
  onChannelSelect: (nextChannelId: string, groupId: string, channelName: string) => void
}) {
  return (
    <div className="lg:hidden sticky top-0 z-20 bg-background border-b border-border px-4 py-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Forum</h1>
        <Sheet open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="sm" className="lg:hidden">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Open sidebar</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80 p-0">
            <GroupChannelSidebar currentChannelId={channelId} groupId={groupId} onSelectChannel={onChannelSelect} />
          </SheetContent>
        </Sheet>
      </div>
    </div>
  )
}
